import React, { useEffect, useRef } from "react";
import { X, Sparkles, Clock, Zap } from "lucide-react";

/**
 * ComingSoonModal — Reusable "Feature Coming Soon" modal.
 *
 * Props:
 *  - isOpen       : boolean  — controls visibility
 *  - onClose      : () => void
 *  - featureName  : string   — e.g. "Bulk WhatsApp"
 *  - description  : string?  — optional subtitle / teaser line
 *  - icon         : ReactNode? — optional custom icon (defaults to Sparkles)
 *  - accentColor  : string?  — tailwind color key, e.g. "emerald" | "teal" | "blue" (default "emerald")
 *
 * Usage:
 *   <ComingSoonModal
 *     isOpen={showModal}
 *     onClose={() => setShowModal(false)}
 *     featureName="Bulk WhatsApp"
 *     description="Send messages to thousands of contacts in one shot."
 *   />
 */
const ACCENT = {
    emerald: {
        ring: "ring-emerald-500/30",
        glow: "shadow-emerald-500/20",
        badge: "bg-emerald-500/10 text-emerald-300 border-emerald-500/20",
        pill: "bg-emerald-500/20 text-emerald-200",
        btn: "from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 shadow-emerald-500/25",
        dot: "bg-emerald-400",
        orb1: "bg-emerald-500/20",
        orb2: "bg-teal-500/15",
        icon: "text-emerald-400",
        iconBg: "bg-emerald-500/10 border-emerald-500/20",
    },
    teal: {
        ring: "ring-teal-500/30",
        glow: "shadow-teal-500/20",
        badge: "bg-teal-500/10 text-teal-300 border-teal-500/20",
        pill: "bg-teal-500/20 text-teal-200",
        btn: "from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 shadow-teal-500/25",
        dot: "bg-teal-400",
        orb1: "bg-teal-500/20",
        orb2: "bg-cyan-500/15",
        icon: "text-teal-400",
        iconBg: "bg-teal-500/10 border-teal-500/20",
    },
    blue: {
        ring: "ring-blue-500/30",
        glow: "shadow-blue-500/20",
        badge: "bg-blue-500/10 text-blue-300 border-blue-500/20",
        pill: "bg-blue-500/20 text-blue-200",
        btn: "from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 shadow-blue-500/25",
        dot: "bg-blue-400",
        orb1: "bg-blue-500/20",
        orb2: "bg-indigo-500/15",
        icon: "text-blue-400",
        iconBg: "bg-blue-500/10 border-blue-500/20",
    },
    purple: {
        ring: "ring-purple-500/30",
        glow: "shadow-purple-500/20",
        badge: "bg-purple-500/10 text-purple-300 border-purple-500/20",
        pill: "bg-purple-500/20 text-purple-200",
        btn: "from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 shadow-purple-500/25",
        dot: "bg-purple-400",
        orb1: "bg-purple-500/20",
        orb2: "bg-pink-500/15",
        icon: "text-purple-400",
        iconBg: "bg-purple-500/10 border-purple-500/20",
    },
};

export default function ComingSoonModal({
    isOpen,
    onClose,
    featureName = "This Feature",
    description = "We're working hard to bring this to you. Stay tuned for updates!",
    icon: CustomIcon = null,
    accentColor = "emerald",
}) {
    const overlayRef = useRef(null);
    const a = ACCENT[accentColor] || ACCENT.emerald;

    // Close on Escape
    useEffect(() => {
        if (!isOpen) return;
        const handler = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [isOpen, onClose]);

    // Prevent body scroll when open
    useEffect(() => {
        document.body.style.overflow = isOpen ? "hidden" : "";
        return () => { document.body.style.overflow = ""; };
    }, [isOpen]);

    if (!isOpen) return null;

    const handleOverlayClick = (e) => {
        if (e.target === overlayRef.current) onClose();
    };

    const IconEl = CustomIcon
        ? <CustomIcon className={`w-8 h-8 ${a.icon}`} />
        : <Sparkles className={`w-8 h-8 ${a.icon}`} />;

    return (
        <div
            ref={overlayRef}
            onClick={handleOverlayClick}
            className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
            style={{ background: "rgba(0,0,0,0.65)", backdropFilter: "blur(8px)" }}
        >
            {/* Wrapper — positions card + close button together, NOT overflow-hidden */}
            <div
                className="relative w-full max-w-md"
                style={{ animation: "modalIn 0.28s cubic-bezier(0.34,1.56,0.64,1) both" }}
            >
                {/* Close button — outside the overflow-hidden card so it's never clipped */}
                <button
                    onClick={(e) => { e.stopPropagation(); onClose(); }}
                    className="absolute -top-3 -right-3 z-[100] w-8 h-8 flex items-center justify-center rounded-full bg-slate-700 hover:bg-slate-600 border border-white/20 text-white/70 hover:text-white transition-all duration-200 shadow-lg"
                    aria-label="Close"
                >
                    <X className="w-4 h-4" />
                </button>

                {/* Card */}
                <div
                    className={`
                        relative w-full
                        bg-slate-900/95 border border-white/10
                        rounded-2xl overflow-hidden
                        ring-1 ${a.ring}
                        shadow-2xl ${a.glow}
                    `}
                >
                    {/* Ambient orbs — purely decorative */}
                    <div className={`absolute -top-16 -right-16 w-48 h-48 rounded-full blur-3xl pointer-events-none ${a.orb1}`} />
                    <div className={`absolute -bottom-12 -left-12 w-40 h-40 rounded-full blur-3xl pointer-events-none ${a.orb2}`} />

                    {/* Grid texture overlay */}
                    <div
                        className="absolute inset-0 pointer-events-none opacity-[0.03]"
                        style={{
                            backgroundImage: "linear-gradient(white 1px,transparent 1px),linear-gradient(90deg,white 1px,transparent 1px)",
                            backgroundSize: "24px 24px",
                        }}
                    />

                    {/* Content */}
                    <div className="relative z-10 px-8 pt-10 pb-8 flex flex-col items-center text-center">

                        {/* Icon bubble */}
                        <div className={`w-20 h-20 rounded-2xl border flex items-center justify-center mb-6 ${a.iconBg}`}
                            style={{ boxShadow: "0 0 40px rgba(0,0,0,0.3) inset" }}>
                            {/* Spinning ring around icon */}
                            <div className="relative flex items-center justify-center w-full h-full">
                                <div
                                    className={`absolute inset-2 rounded-xl border-2 border-dashed opacity-30 ${a.dot === "bg-emerald-400" ? "border-emerald-400" : a.dot === "bg-teal-400" ? "border-teal-400" : a.dot === "bg-blue-400" ? "border-blue-400" : "border-purple-400"}`}
                                    style={{ animation: "spinSlow 8s linear infinite" }}
                                />
                                {IconEl}
                            </div>
                        </div>

                        {/* Badge */}
                        <span className={`inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest px-3 py-1 rounded-full border mb-4 ${a.badge}`}>
                            <Clock className="w-3 h-3" />
                            Coming Soon
                        </span>

                        {/* Feature name */}
                        <h2 className="text-2xl font-bold text-white mb-2 leading-tight">
                            {featureName}
                        </h2>

                        {/* Description */}
                        <p className="text-sm text-white/50 leading-relaxed mb-7 max-w-xs">
                            {description}
                        </p>

                        {/* Decorative progress-like dots */}
                        <div className="flex items-center gap-2 mb-7">
                            {[0, 1, 2, 3, 4].map((i) => (
                                <div
                                    key={i}
                                    className={`rounded-full transition-all ${i <= 2 ? `${a.dot} w-2 h-2` : "bg-white/15 w-1.5 h-1.5"}`}
                                    style={i <= 2 ? { animation: `pulse 1.6s ease-in-out ${i * 0.2}s infinite` } : {}}
                                />
                            ))}
                        </div>

                        {/* What to expect pills */}
                        <div className="flex flex-wrap justify-center gap-2 mb-8">
                            {["Bulk Sending", "Smart Queuing", "Real-time Status"].map((tag) => (
                                <span key={tag} className={`text-xs px-3 py-1 rounded-full font-medium ${a.pill}`}>
                                    {tag}
                                </span>
                            ))}
                        </div>

                        {/* CTA button */}
                        <button
                            onClick={onClose}
                            className={`w-full py-3 rounded-xl bg-gradient-to-r ${a.btn} text-white font-semibold shadow-lg transition-all duration-200 flex items-center justify-center gap-2`}
                        >
                            <Zap className="w-4 h-4" />
                            Got it, notify me when ready
                        </button>

                    </div>
                </div>{/* end card */}
            </div>{/* end wrapper */}

            {/* Keyframe styles injected inline so no external CSS file needed */}
            <style>{`
                @keyframes modalIn {
                    from { opacity: 0; transform: scale(0.88) translateY(16px); }
                    to   { opacity: 1; transform: scale(1)    translateY(0);    }
                }
                @keyframes spinSlow {
                    from { transform: rotate(0deg);   }
                    to   { transform: rotate(360deg); }
                }
                @keyframes pulse {
                    0%, 100% { opacity: 1;   transform: scale(1);    }
                    50%       { opacity: 0.4; transform: scale(0.75); }
                }
            `}</style>
        </div>
    );
}